import React from "react";
// Custom Outlined Select

// Default
import Select, {
  SingleValue,
  ActionMeta,
  StylesConfig,
  GroupBase,
} from "react-select";

// Mui
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material";

interface Option {
  label: string;
  value: string | number;
  [key: string]: any;
}

interface OutlinedCustomSelectProps {
  label?: string;
  onChange?: (newValue: Option, actionMeta: ActionMeta<Option>) => void;
  value?: SingleValue<Option> | string;
  options?: Option[];
  placeholder?: string;
  name?: string;
  width?: string;
  height?: string;
  required?: boolean;
  labelStyle?: SxProps<Theme>;
  isDisabled?: boolean;
}

const CustomOutlinedSelect = ({
  label,
  onChange,
  value,
  options,
  placeholder,
  name,
  width = "100%",
  height,
  required = false,
  labelStyle,
  isDisabled = false,
}: OutlinedCustomSelectProps) => {
  const selectedOption = React.useMemo(() => {
    if (typeof value === "string" && Array.isArray(options)) {
      return options.find((opt) => opt.value === value) || null;
    }
    return value as SingleValue<Option> | null;
  }, [value, options]);

  const customStyles: StylesConfig<
    string | Option,
    false,
    GroupBase<Option>
  > = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#fcfcfc",
      borderRadius: "8px",
      height: "36px",
      minHeight: "36px",
      // zIndex: 10,
      boxShadow: "none",
      border: state.isFocused ? "2px solid #1976d2" : "1px solid #c3c3c3",
      "&:hover": {
        border: "2px solid #1976d2",
      },
    }),
    menu: (provided) => ({
      ...provided,
      // zIndex: 1000,
      position: "absolute",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#eef4fc" : "#fcfcfc",
      color: "#181B25",
      fontWeight: 500,
      padding: 10,
      fontSize: "15px",
      "&:hover": {
        backgroundColor: "#eef4fc",
      },
      "&:active": {
        backgroundColor: "#eef4fc",
        transition: "none",
      },
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: "#181B25",
      fontWeight: 400,
      fontSize: "15px",
      fontFamily: "Figtree",
      // backgroundColor: state.isFocused ? "#eef4fc" : "#fcfcfc",
      // backgroundColor: "",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#c3c3c3",
      fontWeight: 400,
      fontSize: "15px",
      fontFamily: "Figtree",
      "&:hover": {
        color: "#c3c3c3",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#B0B0B0",
      fontWeight: 400,
      fontSize: "15px",
      fontFamily: "Figtree",
    }),
  };

  return (
    <Box sx={{ width: width, height: height }}>
      {label && (
        <Typography
          variant="body1"
          sx={{ marginBottom: "10px", ...labelStyle }}
        >
          {label} {required && <Typography component="span">*</Typography>}
        </Typography>
      )}
      <Select
        options={options}
        styles={customStyles}
        onChange={(newValue) =>
          onChange?.(newValue as Option, {
            action: "select-option",
            option: newValue as Option,
          })
        }
        value={selectedOption}
        name={name}
        placeholder={placeholder}
        menuPortalTarget={document.body}
        isDisabled={isDisabled}
        components={{
          IndicatorSeparator: () => null,
        }}
      />
    </Box>
  );
};

export default CustomOutlinedSelect;
